/**
 * useTreatmentStatusDropdown Hook
 *
 * Consolidates dropdown filtering logic for treatment status dropdowns.
 * Replaces 14 identical if-else blocks with a single reusable function.
 */

import { useMemo } from 'react';
import {
  admitOrGroup,
  groupValue,
  medicalValue,
  missedValue,
  IGCredit,
} from '../../../constants';
import {
  TREATMENT_STATUS_IDS,
  ADMIT_GROUP_STATUS_IDS,
  TREATMENT_TYPES,
} from '../utils/verificationConstants';

/**
 * Hook to get filtered dropdown options based on selected treatment status
 *
 * @param {number|null} selectedStatusId - Currently selected status ID
 * @param {Array} baseReferenceData - Base dropdown options from Redux
 * @param {string} treatmentType - Type of treatment (INDIVIDUAL, GROUP, DISCHARGE)
 * @param {boolean} isBilled - Whether the treatment has been billed (from iXTmtOrgInvBilledYn or gXTmtOrgInvBilledYn)
 *                              If true, shows original entry + Credit option
 * @returns {Array} Filtered dropdown options
 */
const useTreatmentStatusDropdown = (
  selectedStatusId,
  baseReferenceData,
  treatmentType,
  isBilled = false
) => {
  const filteredOptions = useMemo(() => {
    // Rule 0: If treatment has been billed, keep original entry and add Credit option
    if (isBilled) {
      if (!selectedStatusId || !baseReferenceData) {
        return IGCredit;
      }

      // Find the currently selected entry
      const selectedEntry = baseReferenceData.find(
        (item) => item.id === parseInt(selectedStatusId)
      );

      // Return original entry + Credit option
      if (selectedEntry) {
        // Credit option from IGCredit (id: 23)
        const creditOption = IGCredit.find((item) => item.id === 23);
        return [selectedEntry, creditOption];
      }

      // If no selected entry found, return IGCredit options
      return IGCredit;
    }

    // If no status selected or no reference data, return base data
    if (!selectedStatusId || !baseReferenceData) {
      return baseReferenceData || [];
    }

    const statusId = parseInt(selectedStatusId);
    const {
      ADMIT_GROUP_OR_INDIVIDUAL,
      NO_SHOW,
      GROUP,
      INDIVIDUAL_OR_RESIDENTIAL,
      MEDICAL,
      ATTENDED,
      MISSED,
    } = TREATMENT_STATUS_IDS;

    // Rule 1: If status is "Admit Group or Individual" (15) or "No Show" (17)
    // Return admit or group options
    if (ADMIT_GROUP_STATUS_IDS.includes(statusId)) {
      return admitOrGroup;
    }

    // Rule 2: If status is "Group" (19)
    // Return group value options
    if (statusId === GROUP) {
      return groupValue;
    }

    // Rule 3: If status is Individual/Residential (18), Medical (2), Attended (6), or Missed (3)
    // Return medical or missed values based on what's available
    if (
      statusId === INDIVIDUAL_OR_RESIDENTIAL ||
      statusId === MEDICAL ||
      statusId === ATTENDED ||
      statusId === MISSED
    ) {
      // Check if "Missed" (ID: 3) exists in base reference data
      const hasMissedValue = baseReferenceData?.find((item) => item.id === MISSED);

      if (hasMissedValue) {
        return missedValue;
      } else {
        return medicalValue;
      }
    }

    // Rule 4: For GROUP treatment type, exclude "Missed" option
    if (treatmentType === TREATMENT_TYPES.GROUP) {
      if (
        statusId === INDIVIDUAL_OR_RESIDENTIAL ||
        statusId === MEDICAL ||
        statusId === ATTENDED
      ) {
        return medicalValue;
      }
    }

    // Default: Return base reference data
    return baseReferenceData;
  }, [selectedStatusId, baseReferenceData, treatmentType, isBilled]);

  return filteredOptions;
};

export default useTreatmentStatusDropdown;
